/**
 * 对后端返回的ai选项进行清洗，生成前端需要的选项，并根据selectModel更新选项信息
 */

import { useMemo, useState } from 'react'
import { GET_AI_PROMPTS } from '~/graphqls/gqls'
import {
  AiCreateArtworkModel,
  getAIPrompt,
  getAIPromptVariables,
  getAIPrompt_pictureGraph_aiCreateArtworkOptionMenu_promptTipsOptions,
  SuperType,
} from '~/graphqls/types'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { to400Image } from '~/utils'
import {
  modelCodeAtom,
  optionsAtom,
  resolutionKeyAtom,
  selectModelAtom,
} from './atom'
import { useAtom } from 'jotai'

/** 重组后端options数据，生成前端选项 */
export default function useOptions() {
  const [options] = useAtom(optionsAtom)
  const [selectModel] = useAtom(selectModelAtom)
  const [resolutionKey] = useAtom(resolutionKeyAtom)
  const [modelCode] = useAtom(modelCodeAtom)

  /** 画面描述示例 */
  const [prompts, setPrompts] = useState<
    getAIPrompt_pictureGraph_aiCreateArtworkOptionMenu_promptTipsOptions[]
  >(
    options?.promptTipsOptions as getAIPrompt_pictureGraph_aiCreateArtworkOptionMenu_promptTipsOptions[]
  )

  const getPromtsAction = useImperativeQuery<getAIPrompt, getAIPromptVariables>(
    GET_AI_PROMPTS
  )

  const onRefreshPrompts = () => {
    getPromtsAction({
      model: selectModel,
    })
      .then(res => {
        if (
          res?.data?.pictureGraph?.aiCreateArtworkOptionMenu?.promptTipsOptions
        ) {
          setPrompts(
            res?.data?.pictureGraph?.aiCreateArtworkOptionMenu
              ?.promptTipsOptions as getAIPrompt_pictureGraph_aiCreateArtworkOptionMenu_promptTipsOptions[]
          )
        }
      })
      .catch(() => {})
  }
  /** end */

  /** 模型主题 */
  const modelThemeOptions = useMemo(() => {
    return [
      ...(options?.adModelThemeOptions?.map(item => ({
        ...item,
        name: item?.name || '',
        url: to400Image(item?.url || ''),
        isNew: item?.isNew,
        modelCode: item?.modelCode || 0,
        category: '二次元模型',
        model: AiCreateArtworkModel.ANIME_DIFFUSION,
      })) || []),
      ...(options?.sdModelThemeOptions?.map(item => ({
        ...item,
        name: item?.name || '',
        url: to400Image(item?.url || ''),
        isNew: item?.isNew,
        modelCode: item?.modelCode || 0,
        category: '通用模型',
        model: AiCreateArtworkModel.STABLE_DIFFUSION,
      })) || []),
    ]
  }, [options])

  const curModelThemeOptions = useMemo(() => {
    return modelThemeOptions.filter(item => item.model === selectModel)
  }, [modelThemeOptions, selectModel])
  /** end */

  /** 风格选择 */
  const styleDecorationOptions = useMemo(() => {
    if (selectModel === AiCreateArtworkModel.ANIME_DIFFUSION) {
      return options?.adStyleDecorationOptions || []
    } else if (selectModel === AiCreateArtworkModel.STABLE_DIFFUSION) {
      return options?.sdStyleDecorationOptions || []
    } else {
      return []
    }
  }, [selectModel])
  /** end */

  /** 画面大小 */
  const [curResolution, curSizeOptions] = useMemo(() => {
    const curResolution: Record<
      string,
      {
        width: number
        length: number
        price: number
        membersPrice?: number
        superType: SuperType | null
      }[]
    > = {}
    const curSizeOptions: { name: string; desc: string; url: string }[] = []

    const curModelCodeSizeOptions =
      options?.allSizeOptions?.find(
        item => item?.resolutionKey === resolutionKey
      )?.sizeSetOptions || options?.allSizeOptions?.[0]?.sizeSetOptions

    curModelCodeSizeOptions?.forEach(item => {
      if (!item) return
      curSizeOptions.push({
        name: item.name!,
        desc: item.desc!,
        url: item.url!,
      })
      curResolution[item.name!] = []
      item?.resolutionList?.forEach(resolutionItem => {
        curResolution[item.name!].push({
          width: resolutionItem!.width!,
          length: resolutionItem!.length!,
          price: resolutionItem!.price!,
          membersPrice: resolutionItem?.membersPrice || undefined,
          superType: resolutionItem?.superType || null,
        })
      })
    })

    return [curResolution, curSizeOptions]
  }, [options, resolutionKey])
  /** end */

  /** 角色同人 */
  const characterOptions = useMemo(() => {
    return (
      options?.characterOptions?.map(item => ({
        name: item?.name!,
        key: item?.key!,
        type: item?.type,
        category: item?.category,
        isNew: item?.isNew,
      })) || []
    )
  }, [options?.characterOptions])
  /** end */

  /** Lora模型 */
  const modelFusionOptions = useMemo(() => {
    return (
      options?.modelFusionOptions?.map(item => ({
        name: item?.name!,
        key: item?.key!,
        type: item?.type,
        category: item?.category,
        weight: item?.weight,
        isNew: item?.isNew,
      })) || []
    )
  }, [options?.modelFusionOptions])
  /** end */

  /** 是否支持角色同人和Lora */
  const isSupportLoRA = useMemo(() => {
    if (!!options?.patternModelOptions?.length) {
      return !!options.patternModelOptions.find(
        item => item.modelCode === modelCode
      )?.supportLoRA
    }
    return !!modelThemeOptions.find(item => item.modelCode === modelCode)
      ?.supportLoRA
  }, [modelThemeOptions, modelCode, options])

  useDidUpdateEffect(() => {
    onRefreshPrompts()
  }, [selectModel])

  return {
    /** 画面描述示例 */
    prompts,
    /** 模型主题 */
    modelThemeOptions,
    /** 当前大模型下的模型主题 */
    curModelThemeOptions,
    /** 风格选择 */
    styleDecorationOptions,
    /** 角色同人  */
    characterOptions,
    /** Lora模型 */
    modelFusionOptions,
    /** 刷新推荐词 */
    onRefreshPrompts,
    /** 当前尺寸 */
    curSizeOptions,
    /** 当前分辨率 */
    curResolution,
    /** 是否支持角色同人和Lora */
    isSupportLoRA,
  }
}
